'use client';
import {
  faCircleChevronLeft,
  faCircleChevronRight,
  faPlay,
} from '@fortawesome/pro-regular-svg-icons';
import useEmblaCarousel from 'embla-carousel-react';
import { FC, Fragment, useCallback, useEffect, useState } from 'react';
import YouTubePlayer from '@/components/common/youtube-player/youtube-player';
import { Icon, IconButton, Image, ScrollShadow, ScrollShadowVisibility } from '@/components/ui';
import { SimpleProductInfo } from '@/types/products/simple-product-info';
import { cn } from '@/utils';
import { extractYouTubeVideoId, getYouTubeThumbnailUrl } from '@/utils/extract-youtube-video-id';
import { buildGumletUrl } from '@/utils/gumlet';
import ImagesModal from './images-modal';

type GalleryProps = {
  imageSize?: number;
  thumbnailSize?: number;
  product: SimpleProductInfo;
};

type ThumbnailProps = {
  index: number;
  selected: boolean;
  src: string;
  onClick: () => void;
  thumbnailSize: number;
  isVideo?: boolean;
};

const Thumbnail: FC<ThumbnailProps> = ({
  index,
  src,
  onClick,
  selected,
  thumbnailSize,
  isVideo,
}) => {
  const videoId = isVideo ? extractYouTubeVideoId(src) : null;
  const thumbnailUrl = videoId ? getYouTubeThumbnailUrl(videoId) : src;

  return (
    <div className="relative max-w-fit shrink-0 grow-0 basis-1/5">
      <button
        type="button"
        aria-label={`Show product image ${index + 1}`}
        onClick={onClick}
        className="relative h-full w-full"
      >
        <Image
          alt="product"
          src={thumbnailUrl}
          style={{ maxWidth: thumbnailSize, maxHeight: thumbnailSize }}
          className={cn(
            'h-auto scale-85 transition duration-50 ease-in-out',
            !selected && 'hover:scale-90 hover:border-1 hover:border-gray-300',
            selected && 'border-opacity-50 scale-100 border-1 border-black shadow-md',
          )}
        />
        {isVideo && (
          <div className="pointer-events-none absolute inset-0 flex scale-85 items-center justify-center transition duration-50 ease-in-out group-hover:scale-90">
            <div className="bg-opacity-80 flex h-8 w-8 items-center justify-center rounded bg-red-600">
              <Icon icon={faPlay} size="sm" className="ml-0.5 text-white" />
            </div>
          </div>
        )}
      </button>
    </div>
  );
};

export const Gallery: FC<GalleryProps> = ({ imageSize = 600, thumbnailSize = 100, product }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [visibility, setVisibility] = useState<ScrollShadowVisibility | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);

  const [emblaMainRef, emblaMainApi] = useEmblaCarousel();
  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
  });

  const onThumbClick = useCallback(
    (index: number) => {
      if (!emblaMainApi || !emblaThumbsApi) return;
      emblaMainApi.scrollTo(index);
    },
    [emblaMainApi, emblaThumbsApi],
  );

  const onSelect = useCallback(() => {
    if (!emblaMainApi || !emblaThumbsApi) return;

    setSelectedIndex(emblaMainApi.selectedScrollSnap());
    emblaThumbsApi.scrollTo(emblaMainApi.selectedScrollSnap());
  }, [emblaMainApi, emblaThumbsApi]);

  useEffect(() => {
    if (!emblaMainApi) return;

    onSelect();

    emblaMainApi.on('select', onSelect).on('reInit', onSelect);
  }, [emblaMainApi, onSelect]);

  const logSlidesInView = useCallback((api: typeof emblaThumbsApi) => {
    if (!api) return;
    const value = api.slidesInView();
    if (value[0] === 0) {
      setVisibility('right');
    } else if (value[value.length - 1] === api.slideNodes().length - 1) {
      setVisibility('left');
    } else {
      setVisibility('both');
    }
  }, []);

  useEffect(() => {
    if (emblaThumbsApi) emblaThumbsApi.on('slidesInView', logSlidesInView);
  }, [emblaThumbsApi, logSlidesInView]);

  const openModal = (index: number) => {
    setSelectedIndex(index);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setZoomLevel(1);
  };

  const images =
    product.images.length > 0
      ? product.images.map(img => buildGumletUrl(img))
      : ['/imgs/image-not-found.png'];

  const renderMediaItem = (item: string, itemIndex?: number) => {
    const videoId = extractYouTubeVideoId(item);
    const isCurrentlySelected = itemIndex !== undefined && itemIndex === selectedIndex;

    if (videoId) {
      if (isCurrentlySelected) {
        return (
          <div
            className="relative flex aspect-square w-full items-center justify-center"
            style={{ maxWidth: imageSize, maxHeight: imageSize }}
          >
            <div className="absolute inset-0">
              <YouTubePlayer videoId={videoId} autoplay />
            </div>
          </div>
        );
      }
      return (
        <div
          className="aspect-square w-full"
          style={{ maxWidth: imageSize, maxHeight: imageSize }}
        />
      );
    }
    return (
      <div
        className="relative aspect-square w-full"
        style={{ maxWidth: imageSize, maxHeight: imageSize }}
      >
        <Image
          alt="product"
          src={item}
          classNames={{
            wrapper: 'rounded-2xl !max-w-full h-full w-full',
            img: 'rounded-2xl h-full w-full object-contain',
          }}
        />
      </div>
    );
  };

  const handleMediaClick = (idx: number) => {
    openModal(idx);
  };

  return (
    <div className="w-full max-w-3xl">
      <div className="relative">
        <div ref={emblaMainRef} className="w-full max-w-3xl overflow-hidden">
          <div className="flex">
            {images.map((item, idx) => (
              <Fragment key={idx}>
                <div className="w-full max-w-2xl shrink-0 grow-0 basis-full">
                  <button
                    type="button"
                    aria-label={`Open product image ${idx + 1}`}
                    onClick={() => handleMediaClick(idx)}
                    className="flex w-full justify-center"
                  >
                    {renderMediaItem(item, idx)}
                  </button>
                </div>
              </Fragment>
            ))}
          </div>
        </div>
        {(emblaMainApi?.canScrollPrev() || emblaMainApi?.canScrollNext()) && (
          <>
            <IconButton
              aria-label="Previous image"
              variant="light"
              size="lg"
              isDisabled={!emblaMainApi?.canScrollPrev()}
              className="absolute top-1/2 left-0 z-30 -translate-y-1/2 bg-white/50"
              onClick={() => emblaMainApi?.scrollPrev()}
            >
              <Icon size="xl" icon={faCircleChevronLeft} />
            </IconButton>
            <IconButton
              aria-label="Next image"
              variant="light"
              size="lg"
              isDisabled={!emblaMainApi?.canScrollNext()}
              className="absolute top-1/2 right-0 z-30 -translate-y-1/2 bg-white/50"
              onClick={() => emblaMainApi?.scrollNext()}
            >
              <Icon size="xl" icon={faCircleChevronRight} />
            </IconButton>
          </>
        )}
      </div>
      <div className="mt-4">
        <ScrollShadow
          orientation="horizontal"
          visibility={visibility!}
          className="overflow-hidden"
          ref={emblaThumbsRef}
        >
          <div className="flex gap-2">
            {images.map((item, idx) => {
              const videoId = extractYouTubeVideoId(item);
              return (
                <Thumbnail
                  key={idx}
                  index={idx}
                  src={item}
                  onClick={() => onThumbClick(idx)}
                  selected={idx === selectedIndex}
                  thumbnailSize={thumbnailSize}
                  isVideo={!!videoId}
                />
              );
            })}
          </div>
        </ScrollShadow>
      </div>

      <ImagesModal
        isOpen={isOpen}
        closeModal={closeModal}
        selectedIndex={selectedIndex}
        setSelectedIndex={setSelectedIndex}
        zoomLevel={zoomLevel}
        setZoomLevel={setZoomLevel}
        product={product}
      />
    </div>
  );
};
